import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("mm_token"));
  const [mustReset, setMustReset] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      localStorage.removeItem("mm_token"); setToken(null);
    }).finally(() => setLoading(false));
  }, [token]);

  // Staff-only login: phone/email + password.
  const login = async (identifier, password) => {
    const value = String(identifier || "").trim();
    const payload = value.includes("@")
      ? { username: value.toLowerCase(), password }
      : { phone: value, password };
    const r = await api.post("/auth/login", payload);
    localStorage.setItem("mm_token", r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    setMustReset(!!r.data.must_reset_password);
    return { user: r.data.user, mustReset: !!r.data.must_reset_password };
  };

  const changePassword = async (oldPassword, newPassword) => {
    await api.post("/auth/change-password", { old_password: oldPassword, new_password: newPassword });
    setMustReset(false);
  };

  const requestPasswordReset = async (email) => {
    const r = await api.post("/auth/reset-request", { email });
    return r.data;
  };

  const confirmPasswordReset = async (token, newPassword) => {
    const r = await api.post("/auth/reset-confirm", { token, new_password: newPassword });
    return r.data;
  };

  const requestEmailOtp = async (identifier) => {
    const r = await api.post("/auth/otp/request", { identifier });
    return r.data;
  };

  const verifyEmailOtp = async (identifier, otp) => {
    const r = await api.post("/auth/otp/verify", { identifier, otp });
    localStorage.setItem("mm_token", r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    setMustReset(!!r.data.must_reset_password);
    return { user: r.data.user, mustReset: !!r.data.must_reset_password };
  };

  const requestTrackerOtp = async (email) => {
    const r = await api.post("/auth/track/request", { email });
    return r.data;
  };

  const verifyTrackerOtp = async (email, otp) => {
    const r = await api.post("/auth/track/verify", { email, otp });
    localStorage.setItem("mm_token", r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    setMustReset(false);
    return { user: r.data.user };
  };

  const logout = () => {
    localStorage.removeItem("mm_token");
    setToken(null); setUser(null); setMustReset(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, mustReset, login, logout, changePassword, requestPasswordReset, confirmPasswordReset, requestEmailOtp, verifyEmailOtp, requestTrackerOtp, verifyTrackerOtp }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
